import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import styled from '@emotion/styled';

import {
  StyledText,
  StyledFormGroup,
  StyledInput,
  StyledButton,
  StyledLink,
  StyledCheckboxFormControl,
} from '@selfserve/react-ui';
import { buttonIds, links, routesPaths } from '@selfserve/react-utils';

import { FormValues } from '../feature/login';

const StyledCheckboxGroup = styled(StyledFormGroup)`
  flex-direction: row;
  margin-bottom: 42px;
  justify-content: space-between;
`;

const Link = StyledLink.withComponent('a');

interface Props {
  formik: FormikProps<FormValues>;
  handleLinkClick: (
    link: string
  ) => (event: MouseEvent<HTMLAnchorElement>) => void;
  handleToggleLoginByEmail: () => void;
  isLoading: boolean;
  loginWithEmail: boolean;
}

export const LoginForm = ({
  formik: { handleSubmit, handleChange, handleBlur, values, errors, touched },
  handleToggleLoginByEmail,
  handleLinkClick,
  loginWithEmail,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  return (
    <form onSubmit={handleSubmit}>
      <StyledFormGroup>
        <StyledInput
          id="txtEmail"
          name="email"
          inputLabel={t('common.email')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email}
          error={touched.email ? !!errors.email : false}
          inputProps={{ 'aria-label': t('aria.email') }}
          autoFocus
          fullWidth
        />
        {touched.email && errors.email && (
          <p className="p-error">{errors.email}</p>
        )}
      </StyledFormGroup>
      {!loginWithEmail && (
        <StyledFormGroup>
          <StyledInput
            id="txtPass"
            name="password"
            type="password"
            inputLabel={t('common.password')}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.password}
            error={touched.password ? !!errors.password : false}
            inputProps={{ 'aria-label': t('aria.password') }}
            fullWidth
          />
          {touched.password && errors.password && (
            <p className="p-error">{errors.password}</p>
          )}
        </StyledFormGroup>
      )}
      {!loginWithEmail && (
        <StyledCheckboxGroup isFormControl>
          <StyledCheckboxFormControl
            label={t('common.remember-me')}
            name="rememberMe"
            onClick={handleChange}
            value={values.rememberMe}
          />
          <StyledLink
            to={routesPaths.FORGOT_PASSWORD}
            id={buttonIds.forgotPassword}
          >
            {t('common.forgot-password')}
          </StyledLink>
        </StyledCheckboxGroup>
      )}
      <StyledButton
        id={buttonIds.login}
        type="submit"
        loading={isLoading}
        fullWidth
      >
        {loginWithEmail ? t('common.continue-with-email') : t('common.login')}
      </StyledButton>
      <StyledButton
        onClick={handleToggleLoginByEmail}
        type="button"
        disabled={isLoading}
        isBorderless
        fullWidth
      >
        {loginWithEmail ? t('common.login-with-password') : t('common.login-with-email')}
      </StyledButton>
      <StyledText>
        <span>{t('common.dont-have-account')}</span>{' '}
        <Link
          to=""
          href={links.SIGN_UP}
          id={buttonIds.signUp}
          onClick={handleLinkClick(links.PRICING)}
        >
          {t('common.sign-up')}
        </Link>
      </StyledText>
    </form>
  );
};
